// Pure event → display-state reducer. Each handler returns a new
// BattleDisplayState; no side effects, no DOM access. The exhaustive
// switch over event.type is enforced by the EventType union from
// @origami/shared, so adding a new event variant fails to compile here
// until a handler is added.

import type {
  BattleEvent,
  UnitRef,
  UnitSnapshot,
} from '@origami/shared'
import type {
  BattleDisplayState,
  FloatingNumber,
  ImpactBurst,
  LogEntry,
  UnitDisplayState,
} from './types'

let idCounter = 0
function nextId(prefix: string): string {
  idCounter += 1
  return `${prefix}-${idCounter}`
}

const TRIGGER_ICONS: Record<string, string> = {
  on_unfold: '✦',
  on_strike: '⚔',
  on_creased: '✦',
  on_crumple: '☒',
  on_ally_strike: '✦',
  on_ally_crumple: '☒',
  on_ally_unfolded: '✦',
  passive: '◆',
}

/** Synergy-name lookup for `inferSynergySource`. Maps synergy keys
 *  to the (kanji, display_name) pair shown on the midpoint banner. */
const SYNERGY_LABELS: Record<string, { kanji: string; display: string }> = {
  // Animal axes
  bird:    { kanji: '禽', display: 'Bird Flock' },
  reptile: { kanji: '蛇', display: 'Reptile Coil' },
  mammal:  { kanji: '獣', display: 'Mammal Pack' },
  fish:    { kanji: '魚', display: 'Fish School' },
  insect:  { kanji: '蟲', display: 'Insect Swarm' },
  // Element axes
  wood:    { kanji: '木', display: 'Wood Grove' },
  fire:    { kanji: '火', display: 'Fire Blaze' },
  earth:   { kanji: '土', display: 'Earth Bulwark' },
  wind:    { kanji: '風', display: 'Wind Gust' },
  water:   { kanji: '水', display: 'Water Flow' },
}

export function initialState(): BattleDisplayState {
  return {
    units: {},
    order: { A: [], B: [] },
    teams: {
      A: { letter: 'A', synergies: [], aliveCount: 0 },
      B: { letter: 'B', synergies: [], aliveCount: 0 },
    },
    log: [],
    floatingNumbers: [],
    impactBursts: [],
    currentTurn: 0,
    winner: null,
    finished: false,
    lastAnchor: null,
  }
}

function snapshotToUnit(snap: UnitSnapshot, team: 'A' | 'B'): UnitDisplayState {
  return {
    ref: snap.ref,
    team,
    position: snap.position,
    displayPosition: snap.position,
    unit_id: snap.unit_id,
    star: snap.star,
    hp: snap.hp,
    maxHp: snap.hp,
    atk: snap.atk,
    visualState: 'idle',
    statuses: [],
  }
}

function pushLog(state: BattleDisplayState, entry: Omit<LogEntry, 'id'>): BattleDisplayState {
  // Keep a roughly-full battle's worth of entries (a 13-turn fight emits
  // ~140 log-pushing events; capping at 20 evicts early-game beats —
  // including mid-battle summons — before the player can scroll back to
  // them, even though the entries scroll horizontally and there's no
  // performance concern at 60 nodes).
  const log = [{ id: nextId('log'), ...entry }, ...state.log].slice(0, 60)
  return { ...state, log }
}

function withUnit(
  state: BattleDisplayState,
  ref: UnitRef,
  update: (u: UnitDisplayState) => UnitDisplayState
): BattleDisplayState {
  const u = state.units[ref]
  if (!u) return state
  return { ...state, units: { ...state.units, [ref]: update(u) } }
}

function recomputeAlive(state: BattleDisplayState): BattleDisplayState {
  const aliveA = state.order.A.filter((r) => state.units[r]?.visualState !== 'gone').length
  const aliveB = state.order.B.filter((r) => state.units[r]?.visualState !== 'gone').length
  return {
    ...state,
    teams: {
      A: { ...state.teams.A, aliveCount: aliveA },
      B: { ...state.teams.B, aliveCount: aliveB },
    },
  }
}

function pushFloating(
  state: BattleDisplayState,
  fn: Omit<FloatingNumber, 'id' | 'spawnedAt'>
): BattleDisplayState {
  const floatingNumbers = [
    ...state.floatingNumbers,
    { id: nextId('fn'), spawnedAt: performance.now(), ...fn },
  ]
  return { ...state, floatingNumbers }
}

function pushBurst(
  state: BattleDisplayState,
  burst: Omit<ImpactBurst, 'id' | 'spawnedAt'>
): BattleDisplayState {
  const impactBursts = [
    ...state.impactBursts,
    { id: nextId('burst'), spawnedAt: performance.now(), ...burst },
  ]
  return { ...state, impactBursts }
}

/** Push a source→target projectile and a target-side ability_impact
 *  burst. Caller fires this when a `damage` event arrives whose source
 *  unit is currently in `cast_ability` visualState — i.e. an ability
 *  trigger fired and the consequent damage is landing now. */
function pushAbilityHit(
  state: BattleDisplayState,
  source: UnitRef,
  target: UnitRef,
): BattleDisplayState {
  let next = pushBurst(state, { kind: 'projectile', anchor: target, source })
  next = pushBurst(next, { kind: 'ability_impact', anchor: target, source })
  return next
}

/** Infer which active synergy a sourceless damage/heal event came
 *  from, so handlers can spawn a midpoint banner naming it.
 *
 *  Synergy effects emit `source: null` because they're not
 *  attributable to a single unit. We pick the most likely active
 *  synergy on the team that would have caused the event:
 *    - damage → owner = the OPPOSING team of the target (their
 *      synergy hit the enemy)
 *    - heal   → owner = the SAME team as the target (their
 *      synergy healed an ally)
 *
 *  Imperfect — overlapping synergies can fire for the same trigger
 *  — but the user accepted the trade-off ("less per-unit clarity,
 *  no simulator changes"). When the team has no relevant synergy
 *  active, returns null and the handler falls back to a generic
 *  "✦ Synergy" tag. */
function inferSynergySource(
  state: BattleDisplayState,
  event: BattleEvent,
): { team: 'A' | 'B'; kanji: string; display: string } | null {
  let ownerTeam: 'A' | 'B' | null = null
  let preferredKeys: string[] = []
  if (event.type === 'damage') {
    const targetUnit = state.units[event.target]
    if (!targetUnit) return null
    ownerTeam = targetUnit.team === 'A' ? 'B' : 'A'
    // Damage-flavored synergies (fire_3_splash, fire_5_unfold_aoe,
    // bird_3_crumple_splash, bird_5_rear_strike, wind_5_swap).
    preferredKeys = ['fire', 'bird', 'wind']
  } else if (event.type === 'heal') {
    const targetUnit = state.units[event.target]
    if (!targetUnit) return null
    ownerTeam = targetUnit.team
    // Heal-flavored synergies (water_3, wood_3, fish_3, mammal_3).
    preferredKeys = ['water', 'wood', 'fish', 'mammal']
  } else {
    return null
  }
  const active = state.teams[ownerTeam].synergies
  for (const key of preferredKeys) {
    const hit = active.find((s) => s.key === key)
    if (hit) {
      const lbl = SYNERGY_LABELS[key]
      if (lbl) return { team: ownerTeam, kanji: lbl.kanji, display: lbl.display }
    }
  }
  return null
}

/** Apply a single event to the display state. Pure. */
export function applyEvent(state: BattleDisplayState, event: BattleEvent): BattleDisplayState {
  switch (event.type) {
    case 'battle_start': {
      const units: Record<UnitRef, UnitDisplayState> = {}
      const orderA: UnitRef[] = []
      const orderB: UnitRef[] = []
      for (const snap of event.teams.A) {
        units[snap.ref] = snapshotToUnit(snap, 'A')
        orderA.push(snap.ref)
      }
      for (const snap of event.teams.B) {
        units[snap.ref] = snapshotToUnit(snap, 'B')
        orderB.push(snap.ref)
      }
      const next: BattleDisplayState = {
        ...initialState(),
        units,
        order: { A: orderA, B: orderB },
        teams: {
          A: { letter: 'A', synergies: [], aliveCount: orderA.length },
          B: { letter: 'B', synergies: [], aliveCount: orderB.length },
        },
        currentTurn: 0,
      }
      return pushLog(next, {
        turn: 0,
        text: `battle_start · ${orderA.length} vs ${orderB.length}`,
        accent: 'var(--ink)',
        icon: '◆',
      })
    }

    case 'synergy_applied': {
      const team = event.team
      // Idempotency guard: skipToEnd while paused mid-intro re-applies
      // turn-0 events that already landed. Damage/heal/stat events
      // carry absolute new values so re-application is harmless, but
      // synergies are an accumulating list — a duplicate entry breaks
      // the PlayerScroll seal keys (and double-renders the chip).
      if (
        state.teams[team].synergies.some(
          (s) => s.key === event.key && s.threshold === event.threshold,
        )
      ) {
        return { ...state, lastAnchor: { kind: 'synergy', key: event.key } }
      }
      const next = {
        ...state,
        lastAnchor: { kind: 'synergy' as const, key: event.key },
        teams: {
          ...state.teams,
          [team]: {
            ...state.teams[team],
            synergies: [
              ...state.teams[team].synergies,
              { key: event.key, threshold: event.threshold, description: event.description },
            ],
          },
        },
      }
      const accent = event.axis === 'element' ? 'var(--el-wood-deep)' : 'var(--lavender-deep)'
      return pushLog(next, {
        turn: event.turn,
        text: `synergy_applied: ${event.key} ×${event.threshold} (${team})`,
        accent,
        icon: '🍃',
      })
    }

    case 'turn_start': {
      // Reset transient "still on screen" hit/lunge classes so the next
      // turn's events animate fresh. Don't disturb death/gone units,
      // and let in-flight summoning unfold animations complete — the
      // useReplay summon scheduler dispatches a `set_visual_state →
      // idle` with `from: 'summoning'` guard once unfold finishes.
      const units = Object.fromEntries(
        Object.entries(state.units).map(([ref, u]) => [
          ref,
          u.visualState === 'death' || u.visualState === 'gone' || u.visualState === 'summoning'
            ? u
            : { ...u, visualState: 'idle' as const },
        ])
      ) as Record<UnitRef, UnitDisplayState>
      return { ...state, units, currentTurn: event.turn }
    }

    case 'strike': {
      const lunger = state.units[event.source]
      let next = withUnit(state, event.source, (u) => ({ ...u, visualState: 'attack_release' }))
      // Clash burst at the battlefield midpoint — both paired strikes
      // emit one, but the render layer dedupes (only the most recent
      // 'midpoint' clash within the TTL window is drawn).
      next = pushBurst(next, { kind: 'clash', anchor: 'midpoint' })
      const mult = event.multiplier
      const arrow = mult > 1 ? '↑' : mult < 1 ? '↓' : ''
      return pushLog(next, {
        turn: event.turn,
        text: `strike: ${event.source} → ${event.target} · ${arrow ? `${mult}× ${arrow} ` : ''}${event.damage} dmg`,
        accent: lunger?.team === 'A' ? 'var(--el-water-deep)' : 'var(--el-fire-deep)',
        icon: '⚔',
      })
    }

    case 'damage': {
      const blocked = event.mitigated ?? 0
      let next = withUnit(state, event.target, (u) => ({
        ...u,
        hp: event.new_hp,
        visualState: u.visualState === 'death' || u.visualState === 'gone' ? u.visualState : 'take_hit',
        // damage_reduction (stone_tortoise plate) — flash a steel-blue
        // pulse so the player sees mitigation actually happening,
        // alongside the usual take_hit shake.
        pulseEffect: blocked > 0 ? 'mitigated' : u.pulseEffect,
      }))
      const mult = event.multiplier
      // Elemental matchup burst on the target — reinforces the
      // floating-number ↑/↓ icon (§12.5 line 778) on the strike
      // itself. Only emitted when the damage came from a strike
      // (multiplier defined) and the matchup was non-neutral.
      if (mult === 1.25) {
        // Carry the attacker so renderers can tint the burst (and
        // badge it with the element kanji) in the attacking element.
        next = pushBurst(next, { kind: 'super', anchor: event.target, source: event.source ?? undefined })
      } else if (mult === 0.75) {
        next = pushBurst(next, { kind: 'resist', anchor: event.target, source: event.source ?? undefined })
      }
      // Ability source-clarity:
      //   - source !== null + source is mid-cast → projectile from source
      //     to target plus an ability_impact burst (paper-fold splash
      //     bigger than .clash so the player reads it as "this damage
      //     came from THAT unit's ability").
      //   - source === null → synergy damage. Spawn a midpoint
      //     synergy_tag banner naming the inferred synergy.
      //   - source !== null but NOT mid-cast (a plain strike already
      //     handled above) → no extra burst.
      const sourceUnit = event.source ? state.units[event.source] : null
      const sourceIsCasting = sourceUnit?.visualState === 'cast_ability'
      if (event.source && sourceIsCasting) {
        next = pushAbilityHit(next, event.source, event.target)
      } else if (event.source === null && mult === undefined) {
        const syn = inferSynergySource(next, event)
        next = pushBurst(next, {
          kind: 'synergy_tag',
          anchor: 'midpoint',
          label: syn?.display ?? 'Synergy',
          kanji: syn?.kanji ?? '✦',
          team: syn?.team,
        })
      }
      const variant: FloatingNumber['variant'] =
        mult === 1.25 ? 'damage-up' : mult === 0.75 ? 'damage-down' : 'damage'
      next = pushFloating(next, {
        anchor: event.target,
        text: `-${event.amount}`,
        icon: mult === 1.25 ? '↑' : mult === 0.75 ? '↓' : undefined,
        variant,
      })
      if (blocked > 0) {
        next = pushFloating(next, {
          anchor: event.target,
          text: `-${blocked}`,
          icon: '◈',
          variant: 'note',
        })
      }
      return next
    }

    case 'heal': {
      let next = withUnit(state, event.target, (u) => ({
        ...u,
        hp: event.new_hp,
        pulseEffect: 'healed',
      }))
      next = pushFloating(next, {
        anchor: event.target,
        text: `+${event.amount}`,
        variant: 'heal',
      })
      // Synergy-sourced heal (no source ref) → midpoint banner so the
      // green pulse on the target connects to the synergy that caused
      // it (water_3_creased_heal, wood_3_regrowth, fish_3_frontline_heal,
      // mammal_3_strike_heal). Unit-ability heals (ancient_koi etc.)
      // carry a real source and skip this branch.
      if (event.source === null) {
        const syn = inferSynergySource(next, event)
        next = pushBurst(next, {
          kind: 'synergy_tag',
          anchor: 'midpoint',
          label: syn?.display ?? 'Synergy',
          kanji: syn?.kanji ?? '✦',
          team: syn?.team,
        })
      } else if (event.source) {
        const sourceUnit = state.units[event.source]
        if (sourceUnit?.visualState === 'cast_ability') {
          next = pushAbilityHit(next, event.source, event.target)
        }
      }
      return pushLog(next, {
        turn: event.turn,
        text: `heal: ${event.target} +${event.amount} → ${event.new_hp}`,
        accent: 'var(--mint-deep)',
        icon: '✚',
      })
    }

    case 'trigger_fired': {
      let next = withUnit(state, event.source, (u) => ({ ...u, visualState: 'cast_ability' }))
      next = { ...next, lastAnchor: { kind: 'ability' } }
      return pushLog(next, {
        turn: event.turn,
        text: `${event.trigger}: ${event.source} · ${event.effect}`,
        accent: 'var(--lavender-deep)',
        icon: TRIGGER_ICONS[event.trigger] ?? '✦',
      })
    }

    case 'stat_changed': {
      let next = withUnit(state, event.target, (u) => ({
        ...u,
        atk: event.stat === 'atk' ? event.new_value : u.atk,
        maxHp: event.stat === 'hp' ? Math.max(u.maxHp, event.new_value) : u.maxHp,
        hp: event.stat === 'hp' ? event.new_value : u.hp,
        pulseEffect: event.delta >= 0 ? 'buffed_up' : 'buffed_down',
      }))
      const sign = event.delta >= 0 ? '+' : ''
      next = pushFloating(next, {
        anchor: event.target,
        text: `${sign}${event.delta} ${event.stat.toUpperCase()}`,
        variant: event.delta >= 0 ? 'buff' : 'debuff',
        // Attribute synergy-granted buffs so renderers can color the
        // +N in the synergy's element / animal accent.
        synergyKey:
          state.lastAnchor?.kind === 'synergy' ? state.lastAnchor.key : undefined,
      })
      return next
    }

    case 'status_applied': {
      const next = withUnit(state, event.target, (u) => ({
        ...u,
        statuses: u.statuses.includes(event.status) ? u.statuses : [...u.statuses, event.status],
      }))
      return pushLog(next, {
        turn: event.turn,
        text: `status_applied: ${event.target} · ${event.status}${event.stacks > 1 ? ` ×${event.stacks}` : ''}`,
        accent: 'var(--lavender-deep)',
        icon: '◇',
      })
    }

    case 'status_expired': {
      const next = withUnit(state, event.target, (u) => ({
        ...u,
        statuses: u.statuses.filter((s) => s !== event.status),
      }))
      return pushLog(next, {
        turn: event.turn,
        text: `status_expired: ${event.target} · ${event.status}`,
        accent: 'var(--ink-fade)',
        icon: '◇',
      })
    }

    case 'summon': {
      // Mid-battle summon — the server emits a guaranteed-unique
      // ref (e.g. `B:4#1` when slot 4 already holds a live unit),
      // so the new entry never aliases an existing one.
      const ref = event.ref
      const newUnit: UnitDisplayState = {
        ref,
        team: event.team,
        position: event.position,
        displayPosition: event.position,
        unit_id: event.unit_id,
        star: 1,
        hp: event.stats.hp,
        maxHp: event.stats.hp,
        atk: event.stats.atk,
        // Transient — useReplay reverts to 'idle' after the
        // unfold-in animation finishes.
        visualState: 'summoning',
        statuses: [],
      }
      const teamOrder = state.order[event.team]
      const order = teamOrder.includes(ref)
        ? state.order
        : { ...state.order, [event.team]: [...teamOrder, ref] }
      const next = recomputeAlive({
        ...state,
        units: { ...state.units, [ref]: newUnit },
        order,
      })
      return pushLog(next, {
        turn: event.turn,
        text: `summon: ${event.team} · ${event.unit_id} @${event.position}`,
        accent: 'var(--el-wood-deep)',
        icon: '✦',
      })
    }

    case 'crumple': {
      let next = withUnit(state, event.unit, (u) => ({ ...u, hp: 0, visualState: 'death' }))
      next = recomputeAlive(next)
      return pushLog(next, {
        turn: event.turn,
        text: `crumple: ${event.unit} folds flat`,
        accent: 'var(--ink-fade)',
        icon: '☒',
      })
    }

    case 'summon_capped': {
      // No state mutation — the simulator already dropped the summons;
      // this is purely a chronicle line so the player understands why
      // a trigger that said "spawns two" only produced one.
      const sourceLabel = event.source ?? 'summon'
      return pushLog(state, {
        turn: event.turn,
        text: `summon_capped: ${sourceLabel} · ${event.dropped} ${event.unit_id} dropped (team full)`,
        accent: 'var(--ink-fade)',
        icon: '⊘',
      })
    }

    case 'position_swap': {
      // Two units swap line positions mid-battle. Update displayPosition
      // for both. The replay screen reads displayPosition for layout, so
      // the swap appears as a slide of both units to their new slots.
      const a = state.units[event.unit_a]
      const b = state.units[event.unit_b]
      if (!a || !b) return state
      const aPos = a.displayPosition
      const bPos = b.displayPosition
      let next = withUnit(state, event.unit_a, (u) => ({ ...u, displayPosition: bPos }))
      next = withUnit(next, event.unit_b, (u) => ({ ...u, displayPosition: aPos }))
      // Wind ×5 is the only effect that emits position_swap today, and
      // it can chain (up to four positions per the synergy). Tag the
      // first swap of the chain with a midpoint banner so the player
      // reads the rearrangement as a synergy, not a bug. The handler
      // dedupes by team-window: a second swap on the same team within
      // a short interval skips the banner.
      const team = a.team
      const recent = next.impactBursts.find(
        (b) => b.kind === 'synergy_tag' && b.team === team &&
               performance.now() - b.spawnedAt < 600,
      )
      if (!recent) {
        const synLabel = SYNERGY_LABELS.wind!
        next = pushBurst(next, {
          kind: 'synergy_tag',
          anchor: 'midpoint',
          label: synLabel.display,
          kanji: synLabel.kanji,
          team,
        })
      }
      return pushLog(next, {
        turn: event.turn,
        text: `position_swap: ${event.unit_a} ↔ ${event.unit_b}`,
        accent: 'var(--mint-deep)',
        icon: '⇄',
      })
    }

    case 'slide_forward': {
      // new_order is an array of REFS in front-to-back order.
      // Positions stopped being unique once same-slot summons could
      // land, so the simulator emits refs (each unit has its own
      // unique ref allocated at spawn time).
      let next = state
      event.new_order.forEach((ref, idx) => {
        if (!state.units[ref]) return
        next = withUnit(next, ref, (u) => ({ ...u, displayPosition: idx + 1 }))
      })
      // Mark prior 'death' units as 'gone' once slide_forward has settled.
      const units = Object.fromEntries(
        Object.entries(next.units).map(([ref, u]) => [
          ref,
          u.team === event.team && u.visualState === 'death'
            ? { ...u, visualState: 'gone' as const }
            : u,
        ])
      ) as Record<UnitRef, UnitDisplayState>
      next = recomputeAlive({ ...next, units })
      return pushLog(next, {
        turn: event.turn,
        text: `slide_forward: team ${event.team} re-indexed`,
        accent: 'var(--mint-deep)',
        icon: '⇠',
      })
    }

    case 'turn_end': {
      // Clear lingering hit/lunge so the next turn starts clean — but
      // leave death animations to play out (slide_forward will mark
      // them gone) AND let in-flight summon unfold-in animations
      // complete (summons schedule at offset 0.92, so a chunk of
      // their 1.1s unfold runs past turn_end). The deferred settle
      // in useReplay (with `from: 'summoning'`) flips them to 'idle'
      // when the animation finishes; turn_start mirrors the same
      // exclusion when the next turn begins.
      const units = Object.fromEntries(
        Object.entries(state.units).map(([ref, u]) => [
          ref,
          u.visualState === 'death' ||
          u.visualState === 'gone' ||
          u.visualState === 'summoning'
            ? u
            : { ...u, visualState: 'idle' as const },
        ])
      ) as Record<UnitRef, UnitDisplayState>
      return { ...state, units }
    }

    case 'gold_earned': {
      // Battle-phase grant_gold (gold_fish on_crumple). Pure side-channel
      // data for the post-battle pipeline — no in-replay visual state
      // changes today. Log so the chronicle shows the payout in the
      // post-mortem; if we later want a coin-burst float it would slot
      // in here using event.unit as the anchor.
      return pushLog(state, {
        turn: event.turn,
        text: `+${event.amount}g · ${event.timing === 'next_round' ? 'next round' : 'now'} · ${event.team}`,
        accent: 'var(--gold, #d4a017)',
        icon: '¥',
      })
    }
    case 'battle_end': {
      const winner = event.winner
      const units = Object.fromEntries(
        Object.entries(state.units).map(([ref, u]) => {
          // Leave terminal states + in-flight summons alone; the
          // unfold animation needs to finish before this unit moves
          // to victory/idle. The deferred summon-settle in useReplay
          // handles the final transition.
          if (
            u.visualState === 'gone' ||
            u.visualState === 'death' ||
            u.visualState === 'summoning'
          ) {
            return [ref, u]
          }
          const nextState: UnitDisplayState['visualState'] =
            winner !== 'tie' && u.team === winner ? 'victory' : 'idle'
          return [ref, { ...u, visualState: nextState }]
        })
      ) as Record<UnitRef, UnitDisplayState>
      const next = { ...state, units, winner, finished: true }
      return pushLog(next, {
        turn: event.turn,
        text: `battle_end · winner ${winner} · ${event.surviving_count} survivors`,
        accent: 'var(--vermillion-deep)',
        icon: '★',
      })
    }
  }
}

/** Apply many events at once — used by skipToEnd. */
export function applyEvents(state: BattleDisplayState, events: BattleEvent[]): BattleDisplayState {
  return events.reduce(applyEvent, state)
}
